import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import AdminPortalShell from "@/components/admin/AdminPortalShell";
import ImportFilePicker from "@/components/admin/ImportFilePicker";
import ImportPreviewTable from "@/components/admin/ImportPreviewTable";
import ImportErrorTable from "@/components/admin/ImportErrorTable";
import {
  commitImport,
  previewImport,
  type ImportPreview,
} from "@/lib/admin-api";
import { ApiError } from "@/lib/api";
import { track } from "@/lib/analytics";

/**
 * apps/web :: pages :: AdminQuestionImportPage
 *
 * `/admin/questions/import` — bulk CSV import flow.
 *
 *   Step A — pick a file, click "Preview". Calls /import/preview, stages
 *            the preview locally, renders the rows + (when present)
 *            errors. Disables Commit while errors are present.
 *   Step B — when rowsValid > 0 AND rowsInvalid === 0, the "Commit N rows"
 *            button is enabled. Calls /import/commit, success message,
 *            then navigates back to /admin/questions.
 *
 * Analytics:
 *   - admin.import.previewed { rows_parsed, rows_valid, rows_invalid }
 *   - admin.import.committed { rows_inserted }
 *   - admin.import.failed    { stage, error_code }
 *
 * The catalog enforces NO question_text / NO email keys via @ts-expect-error
 * tests; we never pass raw text or PII through this page.
 */

const ANALYTICS_PAGE = "import";

interface UiState {
  kind: "idle" | "previewing" | "preview-ready" | "committing" | "done";
  errorMessage?: string;
  preview?: ImportPreview;
  file?: File | null;
}

function classifyError(err: unknown): "network" | "auth" | "validation" | "server" {
  if (err instanceof ApiError) {
    if (err.status === 401 || err.status === 403) return "auth";
    if (err.status === 400 || err.status === 409 || err.status === 410 || err.status === 413)
      return "validation";
    return "server";
  }
  return "network";
}

export default function AdminQuestionImportPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [state, setState] = useState<UiState>({ kind: "idle" });
  const [pickedFile, setPickedFile] = useState<File | null>(null);
  const [pickerError, setPickerError] = useState<string | null>(null);

  useEffect(() => {
    // The closed-interface ADMIN_PAGE_VIEWED params accept exactly
    // 'list' | 'editor-new' | 'editor-edit'. We can't add 'import' without
    // widening the catalog (separate PR). The new admin.import.previewed
    // event below carries the same intent — bookend per page-mount.
    void ANALYTICS_PAGE;
  }, []);

  const onFileSelected = (file: File | null, error: string | null) => {
    setPickedFile(file);
    setPickerError(error);
    if (file == null) {
      setState({ kind: "idle" });
    }
  };

  const onPreview = async () => {
    if (!pickedFile) return;
    setState({ kind: "previewing", file: pickedFile });
    try {
      const preview = await previewImport(pickedFile);
      track("admin.import.previewed", {
        rows_parsed: preview.summary.rowsParsed,
        rows_valid: preview.summary.rowsValid,
        rows_invalid: preview.summary.rowsInvalid,
      });
      setState({ kind: "preview-ready", preview, file: pickedFile });
    } catch (err) {
      const code = classifyError(err);
      track("admin.import.failed", { stage: "parse", error_code: code });
      setState({
        kind: "idle",
        errorMessage:
          err instanceof Error ? err.message : t("admin.import.errors.unknown"),
        file: pickedFile,
      });
    }
  };

  const onCommit = async () => {
    if (state.kind !== "preview-ready" || !state.preview) return;
    const token = state.preview.token;
    setState({ kind: "committing", preview: state.preview });
    try {
      const result = await commitImport(token);
      track("admin.import.committed", { rows_inserted: result.rowsInserted });
      setState({ kind: "done" });
      // Brief success render then navigate to the list view.
      navigate("/admin/questions", {
        state: {
          toast: t("admin.import.committed.toast", {
            count: result.rowsInserted,
          }),
        },
      });
    } catch (err) {
      const code = classifyError(err);
      track("admin.import.failed", { stage: "commit", error_code: code });
      setState({
        kind: "preview-ready",
        preview: state.preview,
        errorMessage:
          err instanceof Error ? err.message : t("admin.import.errors.unknown"),
      });
    }
  };

  const onCancel = () => {
    setPickedFile(null);
    setPickerError(null);
    setState({ kind: "idle" });
  };

  const previewReady = state.kind === "preview-ready" && state.preview;
  const summary = state.preview?.summary;
  const hasErrors = (summary?.rowsInvalid ?? 0) > 0;
  const hasValid = (summary?.rowsValid ?? 0) > 0;
  const canCommit = previewReady && hasValid && !hasErrors;

  return (
    <AdminPortalShell
      title={t("admin.import.title")}
      subtitle={t("admin.import.subtitle")}
      testId="admin-import-page"
    >
      <div className="space-y-lg">
        {/* Step A — file picker + preview action */}
        {state.kind !== "preview-ready" && state.kind !== "committing" && (
          <section className="rounded-md border border-neutral-200 bg-white p-md">
            <ImportFilePicker
              onFileSelected={onFileSelected}
              disabled={state.kind === "previewing"}
            />
            {pickerError && (
              <p
                role="alert"
                aria-live="polite"
                data-testid="admin-import-picker-error"
                className="mt-sm text-xs text-danger"
              >
                {pickerError}
              </p>
            )}
            {state.errorMessage && (
              <p
                role="alert"
                aria-live="polite"
                data-testid="admin-import-preview-error"
                className="mt-sm rounded-md border border-danger/40 bg-danger/5 p-sm text-xs text-danger"
              >
                {state.errorMessage}
              </p>
            )}
            <div className="mt-md flex gap-sm">
              <button
                type="button"
                data-testid="admin-import-preview-cta"
                disabled={!pickedFile || state.kind === "previewing"}
                onClick={() => {
                  void onPreview();
                }}
                className="inline-flex items-center rounded-md border border-brand-primary bg-brand-primary px-md py-sm text-sm font-semibold text-white transition-colors hover:bg-brand-primary/90 disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
              >
                {state.kind === "previewing"
                  ? t("admin.import.preview.cta.loading")
                  : t("admin.import.preview.cta.idle")}
              </button>
            </div>
          </section>
        )}

        {/* Step B — preview-ready: show summary + tables, allow commit */}
        {previewReady && state.preview && (
          <section className="space-y-md">
            <div
              data-testid="admin-import-summary"
              className="rounded-md border border-brand-primary/20 bg-white p-md text-sm"
            >
              <p>
                <strong>{t("admin.import.summary.parsed")}:</strong>{" "}
                {state.preview.summary.rowsParsed}
              </p>
              <p className="text-success">
                <strong>{t("admin.import.summary.valid")}:</strong>{" "}
                {state.preview.summary.rowsValid}
              </p>
              <p className={hasErrors ? "text-danger" : undefined}>
                <strong>{t("admin.import.summary.invalid")}:</strong>{" "}
                {state.preview.summary.rowsInvalid}
              </p>
            </div>

            {hasErrors && <ImportErrorTable errors={state.preview.errors} />}

            <ImportPreviewTable rows={state.preview.rows} />

            {state.errorMessage && (
              <p
                role="alert"
                aria-live="polite"
                data-testid="admin-import-commit-error"
                className="rounded-md border border-danger/40 bg-danger/5 p-sm text-xs text-danger"
              >
                {state.errorMessage}
              </p>
            )}

            <div className="flex flex-wrap gap-sm">
              <button
                type="button"
                data-testid="admin-import-commit-cta"
                disabled={!canCommit}
                onClick={() => {
                  void onCommit();
                }}
                className="inline-flex items-center rounded-md border border-brand-primary bg-brand-primary px-md py-sm text-sm font-semibold text-white transition-colors hover:bg-brand-primary/90 disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
              >
                {t("admin.import.commit.cta", {
                  count: state.preview.summary.rowsValid,
                })}
              </button>
              <button
                type="button"
                data-testid="admin-import-cancel-cta"
                onClick={onCancel}
                className="inline-flex items-center rounded-md border border-neutral-300 bg-white px-md py-sm text-sm font-semibold text-neutral-800 transition-colors hover:bg-neutral-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
              >
                {t("admin.import.cancel.cta")}
              </button>
            </div>
          </section>
        )}

        {state.kind === "committing" && (
          <p
            role="status"
            aria-live="polite"
            data-testid="admin-import-committing"
            className="rounded-md border border-neutral-200 bg-white p-md text-sm text-neutral-700"
          >
            {t("admin.import.committing")}
          </p>
        )}
      </div>
    </AdminPortalShell>
  );
}
